import { Global, css } from '@emotion/react'
import { Link, graphql, useStaticQuery } from 'gatsby'
import { ComponentPropsWithoutRef, Fragment } from 'react'
import { useInView } from 'react-intersection-observer'

import { useNavContext } from '@/contexts/navContext'
import { DatoLink } from '@/features/links'
import { LogoHorizontal } from '@/features/logo'
import { absoluteFill, mq } from '@/theme/mixins'
import { breakpoints, colors } from '@/theme/variables'

import MobileNavButton from './MobileNavButton'

type Props = ComponentPropsWithoutRef<'nav'>

export const MainNav = ({ ...props }: Props) => {
  const { data } = useStaticQuery<Queries.MainNavQuery>(graphql`
    query MainNav {
      data: datoCmsMainNav {
        links {
          ... on DatoCmsPageLink {
            ...PageLink
          }
          ... on DatoCmsLightboxLink {
            ...LightboxLink
          }
        }
      }
    }
  `)
  const { inView, ref: scrolledRef } = useInView({
    initialInView: true,
  })
  const isScrolled = !inView
  const { mobileNavIsOpen, setMobileNavIsOpen } = useNavContext()
  const styles = {
    global: css`
      :root {
        --nav-height-max: calc(2rem + var(--fs-60) * 1.3);
        --nav-height-min: calc(0.5rem + var(--fs-36) * 1.3);
        --nav-height-flex: var(--nav-height-max);
        ${isScrolled &&
        css`
          --nav-height-flex: var(--nav-height-min);
        `}
      }
    `,
    sizer: css`
      height: var(--nav-height-max);
    `,
    nav: css`
      position: fixed;
      z-index: 10;
      top: 0;
      left: 0;
      width: 100%;
      background: ${colors.gray20};
      display: flex;
      justify-content: space-between;
      padding: 1rem 3em 1rem 2em;
      box-sizing: border-box;
      transition: padding 300ms ease;
      ${isScrolled &&
      css`
        padding-top: 0.25rem;
        padding-bottom: 0.25rem;
      `}
      ${mq().ml} {
        padding-right: 2em;
      }
      ${mq().s} {
        padding-left: 1em;
        padding-right: 1em;
      }
      &:before {
        content: '';
        ${absoluteFill};
        background: ${colors.gray20};
        z-index: 10;
      }
    `,
    logoLink: css`
      z-index: 10;
      display: flex;
    `,
    logo: css`
      font-size: var(--fs-60);
      height: 1em;
      margin: 0.1em 0 0.2em;
      transition: height 300ms ease;
      ${isScrolled &&
      css`
        font-size: var(--fs-36);
      `}
    `,
    list: css`
      flex: 1;
      display: flex;
      list-style: none;
      margin: 0;
      padding: 0;
      align-items: center;
      justify-content: flex-end;
      column-gap: 2em;
      z-index: 10;
      ${mq().ml} {
        position: fixed;
        z-index: 9;
        opacity: 0;
        top: var(--nav-height-flex);
        right: 0;
        width: 100%;
        height: calc(100% - var(--nav-height-flex));
        transform: translate3d(0, -50%, 0);
        pointer-events: none;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        row-gap: 1em;
        box-sizing: border-box;
        padding: 0 var(--margin);
        border-bottom: 3px solid ${colors.green};
        background: ${colors.gray15};
        transition:
          opacity 200ms ease,
          transform 300ms ease-out;
        ${mobileNavIsOpen &&
        css`
          opacity: 1;
          transform: translate3d(0, 0, 0);
          pointer-events: all;
        `}
      }
    `,
    listItem: css``,
    link: css`
      color: #fff;
      text-decoration: none;
      text-transform: uppercase;
      font-weight: 400;
      font-size: var(--fs-18);
      padding: 0.333em 0;
      margin: 0.5em 0 0.333em;
      @media (hover: hover) {
        &:hover {
          color: ${colors.greenLight};
        }
      }
    `,
    mobileNavButton: css`
      justify-self: flex-end;
      z-index: 10;
    `,
  }
  return (
    <Fragment>
      <Global styles={styles.global} />
      <div
        ref={scrolledRef}
        css={styles.sizer}
      />
      <nav
        css={styles.nav}
        {...props}
      >
        <Link
          css={styles.logoLink}
          to="/"
        >
          <LogoHorizontal
            isScrolled={isScrolled}
            css={styles.logo}
          />
        </Link>
        <MobileNavButton
          css={styles.mobileNavButton}
          breakpoint={breakpoints.ml}
        />
        <ul css={styles.list}>
          {data?.links?.map(link => (
            <li
              css={styles.listItem}
              key={link?.id}
            >
              <DatoLink
                css={styles.link}
                onClick={() => setMobileNavIsOpen(false)}
                data={link}
              />
            </li>
          ))}
        </ul>
      </nav>
    </Fragment>
  )
}
